import React, { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Phone, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { getGuideBySlug, guideField, guideWordCount, GUIDES } from "../data/guides";
import { BRAND } from "../mock";
import { useSeo, SEO_BASE } from "../lib/seo";
import { useLang } from "../i18n/LanguageContext";
import BookingWidget from "../components/BookingWidget";

const UI = {
  de: { back: "Ratgeber", read: "Min. Lesezeit", cta: "Jetzt Fixpreis berechnen", faq: "Häufige Fragen", ready: "Bereit für Ihren Transfer?", book: "Online buchen", more: "Weitere Ratgeber", bullets: ["Garantierter Fixpreis", "Flugverfolgung & Wartezeit inklusive", "24/7 verfügbar"] },
  en: { back: "Guides", read: "min read", cta: "Calculate your fixed price", faq: "Frequently asked questions", ready: "Ready for your transfer?", book: "Book online", more: "More guides", bullets: ["Guaranteed fixed price", "Flight tracking & waiting time included", "Available 24/7"] },
  fr: { back: "Guides", read: "min de lecture", cta: "Calculer votre prix fixe", faq: "Questions fréquentes", ready: "Prêt pour votre transfert ?", book: "Réserver en ligne", more: "Autres guides", bullets: ["Prix fixe garanti", "Suivi de vol et temps d'attente inclus", "Disponible 24h/24"] },
  it: { back: "Guide", read: "min di lettura", cta: "Calcola il prezzo fisso", faq: "Domande frequenti", ready: "Pronto per il tuo transfer?", book: "Prenota online", more: "Altre guide", bullets: ["Prezzo fisso garantito", "Monitoraggio volo e attesa inclusi", "Disponibile 24/7"] },
};

const Guide = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang: rawLang } = useLang();
  const lang = ["de", "en", "fr", "it"].includes(rawLang) ? rawLang : "de";
  const ui = UI[lang];
  const guide = getGuideBySlug(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { if (!guide) navigate("/ratgeber", { replace: true }); }, [guide, navigate]);

  const words = useMemo(() => (guide ? guideWordCount(guide, lang) : 0), [guide, lang]);

  const h1 = guide ? guideField(guide, "h1", lang) : "";
  const title = guide ? guideField(guide, "title", lang) : "";
  const description = guide ? guideField(guide, "description", lang) : "";
  const sections = guide ? guideField(guide, "sections", lang) : [];
  const faq = guide ? guideField(guide, "faq", lang) : [];

  useSeo(
    guide
      ? {
          title,
          description,
          path: `/ratgeber/${guide.slug}`,
          image: guide.image,
          jsonLd: [
            { "@context": "https://schema.org", "@type": "Article", headline: h1, description, image: guide.image, author: { "@type": "Organization", name: BRAND.name }, publisher: { "@type": "Organization", name: BRAND.name, url: SEO_BASE }, inLanguage: lang, mainEntityOfPage: `${SEO_BASE}/ratgeber/${guide.slug}` },
            { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq.map((f) => ({ "@type": "Question", name: f[0], acceptedAnswer: { "@type": "Answer", text: f[1] } })) },
            { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SEO_BASE + "/" },
              { "@type": "ListItem", position: 2, name: ui.back, item: SEO_BASE + "/ratgeber" },
              { "@type": "ListItem", position: 3, name: h1, item: `${SEO_BASE}/ratgeber/${guide.slug}` },
            ] },
          ],
        }
      : {}
  );

  if (!guide) return null;
  const readMin = Math.max(3, Math.round(words / 200));
  const related = GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 3);

  return (
    <div className="pt-[68px] min-h-screen bg-white" data-testid="guide-page">
      <div className="relative bg-[#0B1533] text-white overflow-hidden">
        <img src={guide.image} alt={h1} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533] via-[#0B1533]/85 to-[#0B1533]/70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 font-body text-sm text-white/60 hover:text-white transition-colors mb-6"><ArrowLeft size={16} /> {ui.back}</Link>
          <p className="font-body text-sm text-[#C9A24B] font-semibold mb-3">{guideField(guide, "tag", lang)}</p>
          <h1 className="font-heading font-bold tracking-tight text-3xl sm:text-5xl leading-[1.08]" data-testid="guide-h1">{h1}</h1>
          <p className="font-body text-white/75 text-base mt-5 max-w-2xl leading-relaxed">{description}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <span className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2 font-body text-sm"><Clock size={15} className="text-[#2B4BFF]" /> {readMin} {ui.read}</span>
            <Link to="/booking" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-2.5 hover:bg-[#1E38D8] transition-colors">{ui.cta} <ArrowRight size={16} /></Link>
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 border border-white/20 text-white font-body font-semibold rounded-full px-6 py-2.5 hover:bg-white/10 transition-colors"><Phone size={16} /> {BRAND.phone}</a>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20">
        <BookingWidget />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <article>
          {sections.map((s, i) => (
            <section key={i} className="mb-9">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0B1533] mb-4">{s.h}</h2>
              {s.paras.map((p, k) => (<p key={k} className="font-body text-[#3d4257] leading-[1.8] mb-4">{p}</p>))}
            </section>
          ))}

          <section className="mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0B1533] mb-4">{ui.faq}</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faq.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="bg-[#F5F2ED] border border-[#E9E5DE] rounded-2xl px-5">
                  <AccordionTrigger className="font-heading font-semibold text-[#0B1533] text-left hover:no-underline py-4">{f[0]}</AccordionTrigger>
                  <AccordionContent className="font-body text-[#5A607A] leading-relaxed pb-4">{f[1]}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="rounded-3xl bg-[#0B1533] p-8 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <h3 className="font-heading font-bold text-2xl">{ui.ready}</h3>
              <ul className="mt-3 space-y-1.5">
                {ui.bullets.map((x) => (<li key={x} className="flex items-center gap-2 font-body text-sm text-white/80"><CheckCircle2 size={16} className="text-[#25D366]" /> {x}</li>))}
              </ul>
            </div>
            <Link to="/booking" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#1E38D8] transition-colors shrink-0">{ui.book} <ArrowRight size={16} /></Link>
          </div>
        </article>
      </div>

      <div className="bg-[#F5F2ED] border-t border-[#E9E5DE]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-heading font-semibold text-xl text-[#0B1533] mb-5">{ui.more}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((g) => (
              <Link key={g.slug} to={`/ratgeber/${g.slug}`} data-testid={`related-guide-${g.slug}`} className="group flex flex-col rounded-2xl border border-[#E9E5DE] bg-white overflow-hidden hover:border-[#2B4BFF] hover:elev-soft transition-all">
                <div className="h-28 overflow-hidden"><img src={g.image} alt={guideField(g, "h1", lang)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" /></div>
                <div className="px-4 py-3">
                  <span className="font-body text-xs font-semibold text-[#2B4BFF]">{guideField(g, "tag", lang)}</span>
                  <p className="font-heading font-semibold text-sm text-[#0B1533] leading-snug mt-1">{guideField(g, "h1", lang)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guide;
